import type { DatabaseDriver } from '@w/db'

/**
 * Dev-only handle on every live browser SQLite database, published as
 * `globalThis.__db` so a debugger can run real SQL against live state.
 *
 * Browser SQLite is Turso WASM running inside a (Shared)Worker — the page only
 * holds a comlink proxy, so nothing in the database is reachable from a
 * DevTools console or `Runtime.evaluate` on the page target. A `:memory:`
 * database has no OPFS file to fall back on either, which leaves console
 * breadcrumbs as the only view of its contents. This registry closes that gap:
 *
 *   await __db.list()                          // open database names
 *   await __db.tables()                        // sqlite_master of the default db
 *   await __db.query('select * from cards')    // arbitrary SQL
 *   await __db.get('flashcards-x').query(sql)  // pick a specific database
 *
 * Gated on `import.meta.env.DEV`, which Vite replaces with a literal at build
 * time, so production bundles drop the registry and every database it would
 * have retained.
 */
export type DevDatabaseRegistry = {
  get(name?: string): DatabaseDriver
  list(): string[]
  query<T = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ): Promise<T[]>
  tables(name?: string): Promise<Array<{ name: string; sql: string }>>
}

const databases = new Map<string, DatabaseDriver>()

export function registerDevDatabase<D extends DatabaseDriver>(
  path: string,
  driver: D,
): D {
  if (!import.meta.env.DEV) return driver

  const name = uniqueName(path)
  databases.set(name, driver)
  publishRegistry()
  return trackClose(driver, name)
}

function uniqueName(path: string): string {
  if (!databases.has(path)) return path
  let suffix = 2
  while (databases.has(`${path}#${suffix}`)) suffix += 1
  return `${path}#${suffix}`
}

// Preserves accessors (the shared ref exposes `dialect` as a getter), which a
// spread copy would flatten into a one-time snapshot.
function trackClose<D extends DatabaseDriver>(
  driver: D,
  name: string,
): D {
  return new Proxy(driver, {
    get(target, prop, receiver) {
      if (prop !== 'close')
        return Reflect.get(target, prop, receiver) as unknown
      return async () => {
        databases.delete(name)
        await target.close?.()
      }
    },
  })
}

function publishRegistry(): void {
  const global = globalThis as { __db?: DevDatabaseRegistry }
  global.__db ??= {
    get: getDatabase,
    list: () => [...databases.keys()],
    query: (sql, params) => getDatabase().query(sql, params),
    tables: (name) =>
      getDatabase(name).query(
        "select name, sql from sqlite_master where type = 'table' order by name",
      ),
  }
}

function getDatabase(name?: string): DatabaseDriver {
  if (name === undefined) {
    const [first] = databases.values()
    if (!first)
      throw new Error('No open Turso database — open a story that uses one')
    return first
  }

  const driver = databases.get(name)
  if (!driver)
    throw new Error(
      `Unknown Turso database "${name}". Open: ${[...databases.keys()].join(', ') || '(none)'}`,
    )
  return driver
}
